/**
 * The landing-page experiences. Chosen per locale by the business via the
 * `candy-landing-variant` PostHog payload (locale -> which experience that
 * country sees); `phone-and-link` is today's page and the fallback whenever a
 * locale is unconfigured or the flag is unavailable.
 *
 * The `-v1` keys are the new-UX redesigns (product's "V2" experience) of the
 * three pages; they let a country A/B-test the current design against the new
 * one, e.g. `split: [{ lp: 'phone-and-link', weight: 50 },
 * { lp: 'phone-and-link-v1', weight: 50 }]`, with `afterHours.lp` free to
 * target either design's link-only page.
 *
 * `phone-only-v2` is the LP1 "hybrid" (Sept 2026): the control page's 10-year
 * headline, V1's approved copy and phone numbers, and a new mobile-first layout.
 * `phone-and-link-v2` is the LP2 hybrid (phone + online), English first; a -v2
 * link design collapses to `phone-only-v2` when the locale has no online link.
 */
export type LandingVariant =
  | 'phone-only'
  | 'phone-and-link'
  | 'link-only'
  | 'phone-only-v1'
  | 'phone-and-link-v1'
  | 'link-only-v1'
  | 'phone-only-v2'
  | 'phone-and-link-v2';

export const LANDING_VARIANTS: LandingVariant[] = [
  'phone-only',
  'phone-and-link',
  'link-only',
  'phone-only-v1',
  'phone-and-link-v1',
  'link-only-v1',
  'phone-only-v2',
  'phone-and-link-v2',
];

/**
 * The new-UX designs (V1 and the V2 hybrid). They share the V1 legal footer,
 * which drops the cookie-policy link (DPO-approved 2026-08-27).
 */
export const isNewUxVariant = (variant: LandingVariant): boolean =>
  variant.endsWith('-v1') || variant.endsWith('-v2');

export const DEFAULT_LANDING_VARIANT: LandingVariant = 'phone-and-link';

/**
 * Copy fields a locale may override per variant (see LocaleData.variants).
 * The UX designs for the three variants may differ in wording, not just in
 * which CTAs render, so every user-facing string in the CTA journey is
 * overridable; anything omitted falls back to the locale's base field.
 */
export type LandingVariantCopy = {
  banner?: string;
  heroCopy?: string;
  heroSubCopy1?: string;
  heroSubCopy2?: string;
  registration?: string;
  ctaPhoneText?: string;
  /**
   * Variant-specific dial link. The new-UX variants advertise different phone
   * numbers in some countries so the call centre can attribute calls to the
   * A/B arm — the live design must keep dialling the current number.
   */
  ctaPhoneLink?: string;
  phoneSubCopy?: string;
  onlineCopy?: string;
  /** Desktop-only replacement for `onlineCopy` (e.g. a shorter LP2 heading that fits
   * one line when the two CTA columns sit side by side). Phones keep `onlineCopy`. */
  onlineCopyDesktop?: string;
  ctaOnlineText?: string;
  phoneNumberAsterix?: string[];
  phoneNumberAsterixLinks?: string[];
  /** The three green-tick benefit lines in the new-UX hero. */
  benefitTicks?: string[];
  /** The reworded warranty-parts line under the ticks (no leading asterisk). */
  heroDisclaimer?: string;
  /** "Register within 60 days" info-box heading and body. */
  sixtyDayHeader?: string;
  sixtyDayBody?: string;
  /** link-only-v1 only: the "phone lines are currently closed" line. */
  linesClosedMessage?: string;
  /** phone-only-v2 only: the two-line offer headline (large line, small line). */
  offerHeadline?: string[];
  /** phone-only-v2 only: the line above the benefit ticks. */
  benefitsHeader?: string;
  /** phone-only-v2 only: the heading above the repeated phone button. */
  repeatHeading?: string;
  /** phone-only-v2 only: the repeated phone button's label. */
  ctaPhoneRepeatText?: string;
};
